/**
 * Application initialize file.
 *
 * This file should import application root file.
 *
 * Execute all of module initialization.
 */
import { config } from '@/config'
import { getPlatformResult } from '@/core/native/app'
import { lockPortrait } from '@/core/native/screenOrientation'

import { init as initAtt } from '../core/appTrackingTransparency'
import { i18n, init as initI18n } from '../core/i18n/init'
import { init as initPayment } from '../core/payment'
import { init as initDayjs } from '../lib/dayjs'
import { init as initFirebase } from '../lib/firebase'

if (config.platform.includes('line') && typeof window !== 'undefined') {
  const liffId = config.liffId
  if (!liffId) {
    console.error(
      '[bootstrap] NEXT_PUBLIC_LIFF_ID is not set; LIFF initialization skipped'
    )
  } else {
    /*
     * LINE のログインに使うライブラリを先に読み込んでおく
     * ログインするときに待たされず、画面表示が少し速くなる
     */
    void import('@line/liff').catch(() => {})
    void import('@/line')
      .then(({ initLiff }) => initLiff(liffId))
      .catch(err => {
        console.error('[bootstrap] LIFF initialization failed', err)
      })
  }
}

/*
 * [開発用] LINE のログイン状態を強制リセットするためのヘルパー関数
 * 開発時のデバッグ画面から呼ぶとログアウトして自動で再読み込みする
 * 本番では動かない。動作確認が終わったらこのかたまりごと削除する
 */
if (
  typeof window !== 'undefined' &&
  process.env.NEXT_PUBLIC_ENABLE_DEBUG_CONSOLE === '1'
) {
  const w = window as unknown as {
    __lineDebugSignOut?: () => Promise<void>
  }
  w.__lineDebugSignOut = async () => {
    try {
      const { getAuth, signOut } = await import('firebase/auth')
      await signOut(getAuth()).catch(() => {})
    } catch {
      /* 失敗しても無視 */
    }
    try {
      const { default: liff } = await import('@line/liff')
      if (typeof liff.logout === 'function') {
        liff.logout()
      }
    } catch {
      /* 失敗しても無視 */
    }
    try {
      localStorage.clear()
      sessionStorage.clear()
    } catch {
      /* 失敗しても無視 */
    }
    setTimeout(() => location.reload(), 500)
  }
}

// LIFF ブラウザ内で console/network を見るための eruda を CDN から差し込む。
// NEXT_PUBLIC_ENABLE_DEBUG_CONSOLE=1 のときだけ有効化。本番ビルドには影響しない。
if (
  typeof window !== 'undefined' &&
  process.env.NEXT_PUBLIC_ENABLE_DEBUG_CONSOLE === '1'
) {
  const script = document.createElement('script')
  script.src = 'https://cdn.jsdelivr.net/npm/eruda'
  script.onload = () => {
    const w = window as unknown as { eruda?: { init: () => void } }
    w.eruda?.init()
  }
  script.onerror = () => {
    console.error('[bootstrap] failed to load eruda from CDN')
  }
  document.head.appendChild(script)
}

initI18n()
initDayjs(i18n.language)
initFirebase({
  appId: config.firebase.appId,
  projectId: config.firebase.projectId,
  apiKey: config.firebase.apiKey,
  senderId: config.firebase.senderId,
  measurementId: config.firebase.measurementId,
})
initPayment(
  config.stripeApiKey,
  getPlatformResult({
    ios: config.revenueCatApiKey.ios,
    android: config.revenueCatApiKey.android,
    web: '',
  })
)
initAtt()
// ネイティブアプリの場合、起動時に画面を縦固定にする。
// 動画の全画面再生時のみ横向きを許可し、それ以外は縦固定を維持する。
// Android の MainActivity は android:screenOrientation="portrait" だが、
// @capacitor/screen-orientation の lock() はランタイムで setRequestedOrientation を呼びマニフェストを上書きできるため、
// 起動時の lockPortrait() は競合せず縦を再強制する（src/core/native/screenOrientation/index.ts 参照）。
// bootstrap はトップレベル実行のため await できない。失敗は lockPortrait 内部の catch で処理される。
void lockPortrait()
